import { ControlStateMachine } from "@/core/battle/ControlStateMachine";
import { ControlState, FactionType } from "@/core/types/battle.types";
import { coordsToId } from "@/core/utils/hex.utils";
import { BattleBus } from "@/events/Events";
import { useBattleStore } from "@/store/useBattleStore";
import { shallow } from 'zustand/shallow';

import cursorDefault from '@assets/cursors/cursor_default.png';
import cursorAttack from '@assets/cursors/cursor_attack.png';
import cursorMove from '@assets/cursors/cursor_move.png';
import cursorDenied from '@assets/cursors/cursor_denied.png';
import cursorHourglass from '@assets/cursors/cursor_hourglass.png';

export const CursorType = {
    // Використовуємо змінні замість аліасів
    Default: `url(${cursorDefault}) 0 0, default`,
    Attack:  `url(${cursorAttack}) 0 0, crosshair`,
    Move:    `url(${cursorMove}) 0 16, move`,
    Denied:  `url(${cursorDenied}) 16 16, pointer`,
    Waiting: `url(${cursorHourglass}) 16 16, pointer`,
} as const;

export type CursorType = typeof CursorType[keyof typeof CursorType];

export class CursorManager {
    private hoveredUnitId: string | null = null;
    private hoveredHexId: string | null = null;
    private _pendingUpdate = false;

    // Локальний кеш
    private reachableHexes: Map<string, any> = new Map();
    private targetHexes: string[] = [];
    private units: Map<string, any> = new Map();

    private unsubStore!: () => void;

    private readonly handlers = {
        unitHover: ({ id }: { id: string }) => { this.hoveredUnitId = id; this.updateCursor(); },
        unitOut:   () => { this.hoveredUnitId = null; this.updateCursor(); },
        hexHover:  ({ id }: { id: string }) => { this.hoveredHexId = id; this.updateCursor(); },
        hexOut:    () => { this.hoveredHexId = null; this.updateCursor(); },
    };

    constructor(
        private canvas: HTMLCanvasElement,
        private stateMachine: ControlStateMachine
    ) {
        this.init();
    }

    public set(type: string) {
        this.canvas.style.cursor = type;
    }

    private updateCursor() {
        if (this._pendingUpdate) return;
        this._pendingUpdate = true;
        queueMicrotask(() => {
            this._pendingUpdate = false;
            this._doUpdateCursor();
        });
    }

    private _doUpdateCursor() {
        

        if (this.stateMachine.is(ControlState.SkillTargeting)) {
            // const unitHex = this.hoveredUnitId
            //     ? coordsToId(this.units.get(this.hoveredUnitId)!)
            //     : null;
            const isInRange = this.hoveredHexId ? this.targetHexes.includes(this.hoveredHexId) : false;

            if (!this.hoveredHexId) {
                this.set(CursorType.Default);
            } else {
                const isEnemy = this.hoveredUnitId
                    ? this.units.get(this.hoveredUnitId)?.faction === FactionType.Enemy
                    : false;
                this.set(isEnemy && isInRange ? CursorType.Attack : CursorType.Denied);
            }
        } else {
            if (this.stateMachine.isBlocking() || !this.stateMachine.isBattleActive) {
                this.set(CursorType.Waiting);
            } else {
                const isInRange = this.hoveredHexId
                    ? this.reachableHexes.has(this.hoveredHexId)
                    : false;
                this.set(isInRange ? CursorType.Move : CursorType.Default);
            }
        }
    }

    private init() {
        
        this.unsubStore = useBattleStore.subscribe(
            (state) => ({
                reachableHexes: state.reachableHexesWithCost,
                targetHexes: state.targetHexes,
                units: state.units,
            }),
            ({ reachableHexes, targetHexes, units }) => {
                this.reachableHexes = reachableHexes;
                this.targetHexes = targetHexes;
                this.units = units;
                this.updateCursor();
            },
            { equalityFn: shallow }
            
        );

        this.stateMachine.onStateChange(() => this.updateCursor());

        BattleBus.on('UNIT_HOVER', this.handlers.unitHover);
        BattleBus.on('UNIT_OUT',   this.handlers.unitOut);
        BattleBus.on('HEX_HOVER',  this.handlers.hexHover);
        BattleBus.on('HEX_OUT',    this.handlers.hexOut);
    }

    public destroy(): void {
        this.unsubStore();
        BattleBus.off('UNIT_HOVER', this.handlers.unitHover);
        BattleBus.off('UNIT_OUT',   this.handlers.unitOut);
        BattleBus.off('HEX_HOVER',  this.handlers.hexHover);
        BattleBus.off('HEX_OUT',    this.handlers.hexOut);
    }
}